import { useContext, useMemo } from 'react';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import { resolveSocialFeedForBoard } from './resolveSocialFeedSettings';

interface SocialFeedFeatures {
  /** Whether social feed is active for this board (master toggle + board in target list) */
  feedEnabled: boolean;
  /** Whether extended reactions are active for this board */
  reactionsEnabled: boolean;
  /** Whether save/follow is active for this board */
  saveFollowEnabled: boolean;
  /** Reaction type IDs for posts on this board */
  postReactionTypeIds: string[];
  /** Reaction type IDs for comments on this board */
  commentReactionTypeIds: string[];
  /**
   * Dedup window in minutes for inline view tracking. Shared with LIA's server-side dedup gate
   * (KCOMM-1713), which reads the same config key with different semantics at the edges - see
   * "Configuration -> Two readers, one key" in docs/specs/202604 Inline View Tracking/PRD.md.
   */
  viewTrackingDedupWindowMinutes: number;
}

const DISABLED: SocialFeedFeatures = {
  feedEnabled: false,
  reactionsEnabled: false,
  saveFollowEnabled: false,
  postReactionTypeIds: [],
  commentReactionTypeIds: [],
  viewTrackingDedupWindowMinutes: 0
};

/**
 * Resolves social feed feature flags for a specific board.
 * Reads community settings from AppContext and resolves per-board overrides.
 * Returns all-disabled when the platform flag (enable.AuroraSocialFeed) is off.
 *
 * @param boardDisplayId - The board's displayId slug (e.g. "test-forum")
 * @param isIdea - Whether this is an Idea board (forces reactions off)
 */
export default function useSocialFeedFeatures(
  boardDisplayId: string | undefined,
  isIdea = false
): SocialFeedFeatures {
  const tenant = useContext(TenantContext);
  const { community } = useContext(AppContext);

  return useMemo(() => {
    if (tenant?.publicConfig?.auroraSocialFeedEnabled !== true) {
      return DISABLED;
    }
    const resolved = resolveSocialFeedForBoard(community?.socialFeedSettings, boardDisplayId ?? '');
    return {
      feedEnabled: resolved.enabled,
      reactionsEnabled: resolved.enabled && resolved.reactionsEnabled && !isIdea,
      saveFollowEnabled: resolved.enabled && resolved.saveFollowEnabled,
      postReactionTypeIds: resolved.postReactionTypeIds,
      commentReactionTypeIds: resolved.commentReactionTypeIds,
      viewTrackingDedupWindowMinutes:
        tenant?.publicConfig?.socialFeedViewTrackingDedupWindowMinutes ?? 30
    };
  }, [
    tenant?.publicConfig?.auroraSocialFeedEnabled,
    tenant?.publicConfig?.socialFeedViewTrackingDedupWindowMinutes,
    community?.socialFeedSettings,
    boardDisplayId,
    isIdea
  ]);
}
